import { supabase } from "@/supabase/client";
import { format } from "date-fns";
import { CustomerRow } from "@/utils/excelUtils";
import * as orderService from "@/supabase/order/service/orderService";

// Home.tsx의 Item 타입과 유사하게 정의 (실제 Item 타입 경로에 따라 조정 필요)
interface MigratableItem {
  name: string;
  price: number;
  quantity: number;
  // id?: string; // id는 마이그레이션 시 필요 없을 수 있음
}

// Home.tsx의 CustomerTemplate 타입과 유사하게 정의
interface MigratableCustomerTemplate {
  id: string; // 세션 스토리지에서의 임시 ID (UUID 등)
  nickname: string;
  template: string; // 메시지 템플릿
  items: MigratableItem[];
}

/**
 * 비회원의 sessionStorage에 저장된 고객 데이터를 Supabase로 마이그레이션 (parsed_order 테이블 대상)
 * @param userId 회원 ID (Supabase user.id)
 * @param pseudoUserId 비회원 식별자
 */
export const migrateCustomerData = async (userId: string, pseudoUserId: string): Promise<void> => {
  try {
    console.log("🔄 고객 데이터 마이그레이션 시작 (NewExcel용 parsed_order)");
    
    const highlightedDatesKey = `highlighted_dates_${pseudoUserId}`;
    const savedDates = sessionStorage.getItem(highlightedDatesKey);
    
    if (!savedDates) {
      console.log("🔄 마이그레이션할 고객 데이터 (parsed_order)가 없습니다.");
      return;
    }
    
    const dates = JSON.parse(savedDates) as string[];
    
    // 모든 날짜에 대한 작업을 병렬로 처리
    const migrationPromises = dates.map(async (dateStr) => {
      const storageKey = `parsed_order_${pseudoUserId}_${dateStr}`;
      const savedData = sessionStorage.getItem(storageKey);
      
      if (!savedData) return 0; // 이 날짜의 데이터가 없으면 0건 처리로 간주
      
      try {
        const parsedData = JSON.parse(savedData) as CustomerRow[];
        if (parsedData.length === 0) return 0;
        
        // 한 번에 모든 항목을 삽입하기 위한 준비
        const rowsToInsert = parsedData.map(item => ({
          user_id: userId,
          order_date: dateStr,
          row_order: item.row_order,
          nickname: item.nickname || "",
          name: item.name || "",
          phone_number: item.phone || "",
          address: item.address || "",
          delivery_notes: item.deliveryNotes || ""
        }));
        
        // 한 번의 API 호출로 모든 항목 삽입
        const { error } = await supabase.from("parsed_order").insert(rowsToInsert);
        
        if (error) {
          console.error(`🔄 ${dateStr} 날짜 parsed_order 데이터 일괄 삽입 실패:`, error);
          
          // 일괄 삽입이 실패했을 경우 개별 삽입 시도 (fallback)
          let individualSuccess = 0;
          for (const item of rowsToInsert) {
            const { error: individualError } = await supabase.from("parsed_order").insert(item);
            if (!individualError) individualSuccess++;
          }
          
          if (individualSuccess > 0) {
            console.log(`🔄 ${dateStr} 날짜의 parsed_order 데이터 일괄 삽입 실패 후, 개별 삽입으로 ${individualSuccess}/${parsedData.length}건 완료`);
            return individualSuccess;
          }
          return 0;
        }
        
        console.log(`🔄 ${dateStr} 날짜의 parsed_order 데이터 ${parsedData.length}건 마이그레이션 완료`);
        return parsedData.length;
      } catch (err) {
        console.error(`🔄 ${dateStr} 날짜 parsed_order 데이터 마이그레이션 실패:`, err);
        return 0;
      }
    });
    
    // 모든 날짜의 작업 완료 대기
    const results = await Promise.all(migrationPromises);
    const totalMigrated = results.reduce((sum, count) => sum + count, 0);
    
    console.log(`🔄 고객 데이터 (parsed_order) 마이그레이션 완료 - 총 ${totalMigrated}건`);
  } catch (err) {
    console.error("🔄 고객 데이터 (parsed_order) 마이그레이션 중 오류 발생:", err);
  }
};

/**
 * sessionStorage에 저장된 CustomerTemplate (닉네임별 상품 목록)을 Supabase 주문으로 마이그레이션 (order, order_item 테이블 대상)
 * @param userId 회원 ID
 */
export const migrateSessionCustomerTemplatesToSupabaseOrders = async (userId: string): Promise<void> => {
  console.log("🔄 고객 프로필 (Home.tsx용 CustomerTemplates) Supabase 주문으로 마이그레이션 시작");
  const customerTemplatesKey = `customer-templates-${userId}`; // 회원 ID로 저장된 키 사용
  const savedTemplates = sessionStorage.getItem(customerTemplatesKey);

  if (!savedTemplates) {
    console.log("🔄 마이그레이션할 고객 프로필 데이터가 sessionStorage에 없습니다 (키: ", customerTemplatesKey, ")");
    return;
  }

  try {
    const customerTemplates = JSON.parse(savedTemplates) as MigratableCustomerTemplate[];
    const migrationDate = format(new Date(), "yyyy-MM-dd"); // 마이그레이션 날짜는 오늘로 통일
    let hasMigratedAtLeastOne = false;

    for (const ct of customerTemplates) {
      // ID가 이미 order_로 시작하거나, 닉네임이 없거나, 아이템이 없는 경우는 건너뛰기
      if (ct.id.startsWith('order_')) {
        console.log(`🔄 고객 프로필 [${ct.nickname}] ID [${ct.id}]는 이미 주문 형식이므로 건너뜁니다.`);
        continue;
      }
      if (!ct.nickname || ct.nickname.trim() === "" || ct.nickname === "고객 닉네임") {
        console.log(`🔄 고객 프로필 ID [${ct.id}]는 닉네임이 유효하지 않아 건너뜁니다.`);
        continue;
      }
      if (!ct.items || ct.items.length === 0) {
        console.log(`🔄 고객 프로필 [${ct.nickname}]에 상품이 없어 건너뜁니다.`);
        continue;
      }

      // 아이템 유효성 확인 - 유효하지 않은 아이템(비어있는 이름이나 NaN 가격) 필터링
      const validItems = ct.items.filter(item => 
        item.name && item.name.trim() !== "" && 
        !isNaN(Number(item.price)) && 
        !isNaN(Number(item.quantity)) && 
        Number(item.quantity) > 0
      );
      
      if (validItems.length === 0) {
        console.log(`🔄 고객 프로필 [${ct.nickname}]의 모든 상품이 유효하지 않아 건너뜁니다.`);
        continue;
      }

      const itemsToMigrate = validItems.map(item => ({
        itemName: item.name,
        price: Number(item.price),
        quantity: Number(item.quantity)
      }));

      try {
        console.log(`🔄 고객 [${ct.nickname}]의 상품 ${itemsToMigrate.length}건을 Supabase 주문으로 마이그레이션 중... (날짜: ${migrationDate})`);
        await orderService.createOrderWithMultipleItems(
          userId,
          ct.nickname,
          migrationDate,
          itemsToMigrate,
          ct.template // 메시지 템플릿
        );
        hasMigratedAtLeastOne = true;
        console.log(`✅ 고객 [${ct.nickname}]의 프로필이 Supabase 주문으로 마이그레이션 완료.`);
      } catch (orderCreationError) {
        console.error(`❌ 고객 [${ct.nickname}] 프로필의 Supabase 주문 생성 실패:`, orderCreationError);
      }
    }

    if (hasMigratedAtLeastOne) {
      console.log("✅ 고객 프로필 (CustomerTemplates) Supabase 주문 마이그레이션 완료.");
      
      // Supabase로 성공적 마이그레이션 후, 회원용 sessionStorage 데이터 정리
      sessionStorage.removeItem(customerTemplatesKey);
      sessionStorage.removeItem(`active-customer-${userId}`); 
      console.log("🗑️ 마이그레이션된 고객 프로필 관련 회원용 sessionStorage 정리 완료 (", customerTemplatesKey, ")");
    } else {
      console.log("⚠️ 마이그레이션할 유효한 고객 프로필이 없었습니다.");
    }

  } catch (error) {
    console.error("❌ 고객 프로필 (CustomerTemplates) 마이그레이션 처리 중 오류 발생:", error);
  }
};

/**
 * 비회원 데이터를 회원 데이터로 종합적으로 마이그레이션
 * @param userId 회원 ID (Supabase user.id)
 * @param pseudoUserId 비회원 식별자
 */
export const migrateAllUserData = async (userId: string, pseudoUserId: string): Promise<void> => {
  try {
    console.log("🔄 전체 사용자 데이터 마이그레이션 시작:", { userId, pseudoUserId });
    
    // 1. NewExcel용 고객 주문 데이터 마이그레이션 (parsed_order 테이블)
    await migrateCustomerData(userId, pseudoUserId);
    
    // 2. SessionStorage 내 비회원 데이터를 회원 키로 복사 (customer-templates, recentItems, settings 등)
    await migrateTemplateData(userId, pseudoUserId);
    
    // 3. 복사된 회원 키의 customer-templates를 Supabase 주문으로 마이그레이션 (order, order_item 테이블)
    await migrateSessionCustomerTemplatesToSupabaseOrders(userId);
    
    // 4. 마이그레이션 완료 후 비회원(pseudoUserId) 관련 sessionStorage 데이터 정리
    cleanupNonMemberData(pseudoUserId);
    
    console.log("✅ 전체 사용자 데이터 마이그레이션 성공적 완료");
  } catch (err) {
    console.error("❌ 전체 사용자 데이터 마이그레이션 중 종합 오류 발생:", err);
    // 여기서 에러를 다시 throw하지 않아 SignupForm에서 개별 토스트 메시지를 유지할 수 있도록 함
  }
};

/**
 * 템플릿 및 설정 데이터를 마이그레이션 (SignupForm.tsx에서 사용하는 방식)
 * sessionStorage 내에서 비회원용 키("...-${pseudoUserId}")의 데이터를 읽어 회원용 키("...-${userId}")로 복사
 */
export const migrateTemplateData = async (userId: string, pseudoUserId: string): Promise<void> => {
  try {
    console.log(`🔄 SessionStorage 키 복사 시작 (비회원 "${pseudoUserId}" 키 -> 회원 "${userId}" 키)`);
    
    // 고객 템플릿 데이터 키 (customer-templates-${pseudoUserId} 형식)
    const customerTemplatesPseudoKey = `customer-templates-${pseudoUserId}`;
    let savedCustomerTemplates = sessionStorage.getItem(customerTemplatesPseudoKey);
    
    // 지정된 pseudoUserId로 데이터를 찾지 못한 경우 모든 키를 검색
    if (!savedCustomerTemplates) {
      console.log(`[migrateTemplateData] ⚠️ 지정된 pseudoUserId(${pseudoUserId})로 데이터를 찾지 못함. 다른 키 시도...`);
      
      // 모든 sessionStorage 키 검색
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('customer-templates-') && !key.endsWith(userId)) {
          const foundData = sessionStorage.getItem(key);
          if (foundData) {
            console.log(`[migrateTemplateData] 🔍 대체 키에서 고객 템플릿 데이터 발견: ${key}`);
            savedCustomerTemplates = foundData;
            break; // 첫 번째 발견된 데이터 사용
          }
        }
      }
    }
    
    console.log('savedCustomerTemplates', savedCustomerTemplates);
    if (savedCustomerTemplates) {
      // 유효한 JSON 데이터인지 확인
      try {
        const parsedData = JSON.parse(savedCustomerTemplates);
        if (!Array.isArray(parsedData)) {
          throw new Error("고객 템플릿 데이터가 배열 형식이 아닙니다");
        }
        
        // 기존 회원 데이터가 있으면 백업
        const existingUserData = sessionStorage.getItem(`customer-templates-${userId}`);
        if (existingUserData) {
          console.log(`[migrateTemplateData] 🔄 기존 회원 데이터 백업: ${existingUserData.substring(0, 100)}...`);
          sessionStorage.setItem(`customer-templates-${userId}-backup`, existingUserData);
        }
        
        // 데이터 복사
        sessionStorage.setItem(`customer-templates-${userId}`, savedCustomerTemplates);
        console.log(`[migrateTemplateData] ✔️ 복사됨: customer-templates-* -> customer-templates-${userId}`);
        
        // 활성 고객 ID 복사
        let activeCustomerId = null;
        for (let i = 0; i < sessionStorage.length; i++) {
          const key = sessionStorage.key(i);
          if (key && key.startsWith('active-customer-') && !key.endsWith(userId)) {
            activeCustomerId = sessionStorage.getItem(key);
            if (activeCustomerId) {
              console.log(`[migrateTemplateData] 🔍 활성 고객 ID 발견: ${key} = ${activeCustomerId}`);
              sessionStorage.setItem(`active-customer-${userId}`, activeCustomerId);
              console.log(`[migrateTemplateData] ✔️ 복사됨: ${key} -> active-customer-${userId}`);
              break;
            }
          }
        }
      } catch (e) {
        console.error(`[migrateTemplateData] ❌ 고객 템플릿 데이터 파싱 실패:`, e);
      }
    } else {
      console.log(`[migrateTemplateData] ❔ 원본 고객 템플릿 데이터를 찾을 수 없음`);
    }
    
    // 자주 쓰는 상품 목록 (recentItems_${pseudoUserId} 형식)
    migrateSessionStorageItem(`recentItems_${pseudoUserId}`, `recentItems_${userId}`);
    
    // 기타 설정 데이터 (settings_${pseudoUserId} 형식)
    migrateSessionStorageItem(`settings_${pseudoUserId}`, `settings_${userId}`);

    console.log("🔄 SessionStorage 키 복사 완료");
  } catch (err) {
    console.error("❌ SessionStorage 키 복사 중 오류 발생:", err);
  }
};

/**
 * 단일 sessionStorage 아이템 마이그레이션 헬퍼 함수
 */
const migrateSessionStorageItem = (sourceKey: string, targetKey: string) => {
  const sourceData = sessionStorage.getItem(sourceKey);
  
  // 지정된 키로 데이터를 찾지 못한 경우 유사한 키 검색
  if (!sourceData) {
    const baseKeyParts = sourceKey.split('_');
    if (baseKeyParts.length >= 2) {
      const baseKey = baseKeyParts[0]; // 'recentItems', 'settings' 등
      
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(`${baseKey}_`) && key !== targetKey) {
          const foundData = sessionStorage.getItem(key);
          if (foundData) {
            console.log(`[migrateSessionStorageItem] 🔍 대체 키에서 데이터 발견: ${key} -> ${targetKey}`);
            sessionStorage.setItem(targetKey, foundData);
            return;
          }
        }
      }
    }
    
    console.log(`[migrateSessionStorageItem] ❔ 원본 데이터 없음: ${sourceKey}`);
    return;
  }
  
  sessionStorage.setItem(targetKey, sourceData);
  console.log(`[migrateSessionStorageItem] ✔️ 복사됨: ${sourceKey} -> ${targetKey}`);
};

/**
 * 비회원 데이터 정리 (선택적으로 호출)
 */
export const cleanupNonMemberData = (pseudoUserId: string): void => {
  try {
    console.log(`🔄 비회원(pseudoUserId: ${pseudoUserId}) sessionStorage 데이터 정리 시작`);
    
    // NewExcel용 데이터 (pseudoUserId 기반)
    const highlightedDatesKey = `highlighted_dates_${pseudoUserId}`;
    const savedDates = sessionStorage.getItem(highlightedDatesKey);
    if (savedDates) {
      try {
        const dates = JSON.parse(savedDates) as string[];
        for (const dateStr of dates) {
          sessionStorage.removeItem(`parsed_order_${pseudoUserId}_${dateStr}`);
        }
        sessionStorage.removeItem(highlightedDatesKey);
        console.log(`[cleanupNonMemberData] ✔️ 삭제됨 (NewExcel): ${highlightedDatesKey} 및 관련 parsed_order`);
      } catch (e) {
        console.error(`[cleanupNonMemberData] ⚠️ NewExcel 데이터 정리 중 JSON 파싱 오류: ${highlightedDatesKey}`, e)
        sessionStorage.removeItem(highlightedDatesKey); // 파싱 실패해도 키 자체는 삭제 시도
      }
    } else {
      console.log(`[cleanupNonMemberData] ❔ 삭제할 NewExcel 데이터 없음: ${highlightedDatesKey}`);
    }

    // Home.tsx용 customer templates (pseudoUserId 기반)
    sessionStorage.removeItem(`customer-templates-${pseudoUserId}`);
    sessionStorage.removeItem(`active-customer-${pseudoUserId}`);
    console.log(`[cleanupNonMemberData] ✔️ 삭제됨 (Home): customer-templates-${pseudoUserId}, active-customer-${pseudoUserId}`);

    // Home.tsx용 recentItems, settings (pseudoUserId 기반 - SignupForm.tsx의 원래 로직 참고)
    sessionStorage.removeItem(`recentItems_${pseudoUserId}`);
    sessionStorage.removeItem(`settings_${pseudoUserId}`);
    console.log(`[cleanupNonMemberData] ✔️ 삭제됨 (Home/pseudo): recentItems_${pseudoUserId}, settings_${pseudoUserId}`);
    
    console.log("🔄 비회원 sessionStorage 데이터 정리 완료");
  } catch (err) {
    console.error("❌ 비회원 sessionStorage 데이터 정리 중 오류 발생:", err);
  }
}; 